import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import { RootState } from '../../store/Store';
import BlogTags from '../../components/blog/BlogTags';
import TerraformIcon from '../../components/blog/TerraformIcon';
import { isContentUrl } from '../../models/blog/BlogPost';
import { useLocale } from '../../context/LocaleContext';
import { usePageMeta } from '../../hooks/usePageMeta';
import './BlogPostPage.css';

function formatDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale === 'da' ? 'da-DK' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatTime(iso: string, locale: string): string {
  return new Date(iso).toLocaleTimeString(locale === 'da' ? 'da-DK' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: locale !== 'da',
  });
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

type TocEntry = { level: number; text: string; id: string };

function extractToc(markdown: string): TocEntry[] {
  const entries: TocEntry[] = [];
  const re = /^(#{2,4})\s+(.+)$/gm;
  let m;
  while ((m = re.exec(markdown)) !== null) {
    const level = m[1].length;
    const text = m[2].trim();
    entries.push({ level, text, id: slugify(text) });
  }
  return entries;
}

function renderContentText(text: string) {
  const paras = text.split(/\n\n+/).filter((s) => s.trim());
  if (paras.length === 0) return null;
  return paras.map((para, i) => (
    <p key={i} className={i === 0 ? 'blog-post-lead' : ''}>
      {para.trim()}
    </p>
  ));
}

function getHeadingText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(getHeadingText).join('');
  if (node && typeof node === 'object' && 'props' in node) return getHeadingText((node as React.ReactElement).props.children);
  return '';
}

function PreWithCopy({ children }: { children?: React.ReactNode }) {
  const preRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    const code = preRef.current?.querySelector('code');
    const text = code?.textContent;
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, []);
  return (
    <div className="blog-post-code-wrap">
      <pre ref={preRef}>{children}</pre>
      <button
        type="button"
        className={`blog-post-code-copy ${copied ? 'copied' : ''}`}
        onClick={handleCopy}
        aria-label="Copy code"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

const BlogPostPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { locale, pathFor } = useLocale();
  const posts = useSelector((state: RootState) => state.blog.data);
  const post = (posts || []).find((p) => p.id === id);

  usePageMeta(post?.title ?? 'Blog', post?.excerpt);

  const [fetchedContent, setFetchedContent] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);

  const contentUrl = post?.content && isContentUrl(post.content) ? post.content : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    const rafId = requestAnimationFrame(() => window.scrollTo(0, 0));
    return () => cancelAnimationFrame(rafId);
  }, [id]);

  useEffect(() => {
    if (!contentUrl) {
      setFetchedContent(null);
      setContentError(null);
      return;
    }
    let cancelled = false;
    setFetchedContent(null);
    setContentError(null);
    fetch(contentUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load content: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setFetchedContent(text);
      })
      .catch((err) => {
        if (!cancelled) setContentError(err instanceof Error ? err.message : 'Failed to load content');
      });
    return () => {
      cancelled = true;
    };
  }, [contentUrl]);

  useEffect(() => {
    if (fetchedContent === null) return;
    window.scrollTo(0, 0);
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }, [fetchedContent]);

  const isMarkdown = contentUrl !== null && contentUrl.toLowerCase().endsWith('.md');
  const toc = fetchedContent && isMarkdown ? extractToc(fetchedContent) : [];
  const tocIndexRef = useRef(0);
  useEffect(() => {
    tocIndexRef.current = 0;
  }, [fetchedContent]);
  const markdownComponents = React.useMemo(
    () => ({
      pre: PreWithCopy,
      h2: ({ children }: { children?: React.ReactNode }) => {
        const entry = toc[tocIndexRef.current++];
        const id = entry?.id ?? slugify(getHeadingText(children));
        return <h2 id={id}>{children}</h2>;
      },
      h3: ({ children }: { children?: React.ReactNode }) => {
        const entry = toc[tocIndexRef.current++];
        const id = entry?.id ?? slugify(getHeadingText(children));
        return <h3 id={id}>{children}</h3>;
      },
      h4: ({ children }: { children?: React.ReactNode }) => {
        const entry = toc[tocIndexRef.current++];
        const id = entry?.id ?? slugify(getHeadingText(children));
        return <h4 id={id}>{children}</h4>;
      },
    }),
    [toc]
  );

  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
  const activeRef = useRef<string | null>(null);
  const ignoreScrollUntilRef = useRef(0);
  useEffect(() => {
    if (toc.length === 0) return;
    activeRef.current = null;
    const ids = toc.map((e) => e.id);
    const mark = 120;
    let rafId: number;
    const update = () => {
      if (Date.now() < ignoreScrollUntilRef.current) return;
      let bestId: string | null = null;
      let bestTop = -Infinity;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top;
          if (top <= mark && top > bestTop) {
            bestTop = top;
            bestId = id;
          }
        }
      }
      if (bestId !== activeRef.current) {
        activeRef.current = bestId;
        setActiveHeadingId(bestId);
      }
    };
    const onScroll = () => {
      rafId = requestAnimationFrame(update);
    };
    const timer = setTimeout(update, 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [toc, fetchedContent]);

  useEffect(() => {
    if (!post || post.externalUrl) return;
    const url = `${window.location.origin}/blog/${post.id}`;
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      url,
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [post]);

  if (!id || !post) {
    return <Navigate to={pathFor('blog')} replace />;
  }

  const bodyContent =
    contentUrl !== null
      ? contentError
        ? <p className="blog-post-error">{contentError}</p>
        : fetchedContent !== null
          ? isMarkdown
            ? <div className="blog-post-body-markdown"><ReactMarkdown components={markdownComponents}>{fetchedContent}</ReactMarkdown></div>
            : renderContentText(fetchedContent)
          : <p className="blog-post-loading">Loading content…</p>
      : post.content != null
        ? renderContentText(post.content)
        : null;

  return (
    <main className="blog-post-page" role="main">
      <Link to={pathFor('blog')} className="blog-post-back-link">
        <span className="blog-post-back-arrow">←</span>
        <span>Blog</span>
      </Link>

      <div className="blog-post-layout">
        {toc.length > 0 && (
          <aside className="blog-post-sidebar" aria-label="On this page">
            <span className="blog-post-sidebar-label">On this page</span>
            <ul className="blog-post-toc">
              {toc.map((entry) => (
                <li key={entry.id}>
                  <a
                    href={`#${entry.id}`}
                    className={`toc-level-${entry.level} ${activeHeadingId === entry.id ? 'active' : ''}`}
                    onClick={() => {
                      activeRef.current = entry.id;
                      setActiveHeadingId(entry.id);
                      ignoreScrollUntilRef.current = Date.now() + 400;
                    }}
                  >
                    {entry.text}
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        )}

        <article className="blog-post-article">
          <header className="blog-post-header">
            <h1 className="blog-post-title">
              <span className="blog-post-title-icon" aria-hidden>
                {post.icon === 'terraform' ? (
                  <TerraformIcon size={32} />
                ) : (
                  post.emoji || '📄'
                )}
              </span>
              {post.title}
            </h1>
            <div className="blog-post-meta">
              <time className="blog-post-date" dateTime={post.date}>
                {formatDate(post.date, locale)}
              </time>
              <span className="blog-post-meta-sep">·</span>
              <span className="blog-post-time">{formatTime(post.date, locale)}</span>
            </div>
          </header>

          <hr className="blog-post-divider" />

          <div className="blog-post-body">
            {bodyContent}
          </div>

          <footer className="blog-post-footer">
            <span className="blog-post-footer-label">Tags</span>
            <BlogTags tags={post.tags} className="blog-post-footer-tags" linkToBlogList locale={locale} />
          </footer>
        </article>
      </div>
    </main>
  );
};

export default BlogPostPage;
